import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MainContainer, MainHeaderContainer } from 'layouts/components';
import {
  Button,
  ToastNotification,
  Loading,
  ContentSwitcher,
  Switch
} from 'carbon-components-react';
import DisplayEmails from './components/DisplayEmails';
import { SearchEmailResponse } from './types';
import { Filter16, Renew16 } from '@carbon/icons-react';
import ApplyFilterForm from './components/forms/ApplyFilterForm';
import ReactDOM from 'react-dom';
import { direction_type } from './types';
import { fetchEmailsWithCriteria } from 'services/searchEmail.service';
import { APP } from 'app/AppRoutes';
import Utils from 'utilities/helper.util';
import { filterStr, filterStringReducer } from './components/utils';
import { SendToOtherModal } from 'components';
import { resendToOtherEmailStart } from 'redux/emails';
import { selectResending } from 'redux/emails/selectors';

export const initialValues = {
  recipientContains: '',
  subjectContains: '',
  dateFrom: Utils.getTodayDate(),
  dateTo: Utils.getTodayDate(),
  hourFrom: '12:00',
  amPmFrom: 'AM',
  hourTo: '11:59',
  amPmTo: 'PM',
  status: []
};

const setupFilterString = (values: any, direction: direction_type) => {
  const formData = Object.entries({
    ...values
  }).reduce(filterStringReducer, {});
  const filteredString = filterStr({ formData, direction });
  return filteredString;
};

const SearchEmails: React.FC<any> = () => {
  const dispatch = useDispatch();
  const isResending = useSelector(selectResending);
  const [pageData, setPageData] = useState<any>({
    emails: null,
    pageInfo: {
      count: 0,
      currentPage: 0,
      totalPages: 0
    }
  });

  const isPageLoadedRef = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const initialPageProps = {
    recordsPerPage: 10,
    offset: 0
  };
  const [pageReloadTriggers, setpageReloadTriggers] = useState<any>({
    filter: setupFilterString(initialValues, direction_type.INBOUND),
    direction: direction_type.INBOUND,
    refresh: false,
    pageInfo: initialPageProps
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterValues, setFilterValues] = useState(initialValues);
  const [showSendToOtherModal, setShowSendToOtherModal] = useState<boolean>(
    false
  );
  const [sendToOtherId, setSendToOtherId] = useState<string>();
  const pageWrapper = useRef(null);
  const handleSetDirection = async (direction: direction_type, values: any) => {
    isPageLoadedRef.current = false;
    setpageReloadTriggers({
      ...pageReloadTriggers,
      filter: setupFilterString(values, direction),
      direction,
      pageInfo: initialPageProps
    });
  };

  const onSendToOther = (id: string) => {
    setShowSendToOtherModal(true);
    setSendToOtherId(id);
  };

  const onClickSend = (val: any) => {
    const emailAddress = val;
    const emailId = sendToOtherId;
    dispatch(resendToOtherEmailStart({ emailId, emailAddress }));
    setShowSendToOtherModal(false);
  };

  const handleOpenCloseFilter = (isOpen: boolean) => setIsModalOpen(isOpen);

  const handleSetFilter = async (filter: any) => {
    setpageReloadTriggers({
      ...pageReloadTriggers,
      pageInfo: initialPageProps,
      filter
    });
    setFilterValues(initialValues);
    setIsModalOpen(false);
  };

  const handleFetchNextPage = async (props: any) => {
    setpageReloadTriggers({
      ...pageReloadTriggers,
      pageInfo: {
        ...pageReloadTriggers.pageInfo,
        recordsPerPage: props.pageSize,
        offset: props.page * props.pageSize - props.pageSize
      }
    });
  };

  const handleRefreshPage = async () => {
    setpageReloadTriggers({
      ...pageReloadTriggers,
      refresh: !pageReloadTriggers.refresh
    });
  };

  useEffect(() => {
    const fetchAPI = async () => {
      setIsLoading(true);
      const params = {
        fields: 'id,from,to,subject,createdAt,status',
        order: '-createdAt',
        include: 'Attachment',
        limit: pageReloadTriggers.pageInfo?.recordsPerPage,
        offset: pageReloadTriggers.pageInfo?.offset
      };
      try {
        if (pageReloadTriggers.direction === direction_type.OUTBOUND)
          params.fields = params.fields + ',isResent';

        const response: any = await fetchEmailsWithCriteria({
          ...params,
          filter: pageReloadTriggers.filter,
          direction: pageReloadTriggers.direction
        });
        const responseType =
          pageReloadTriggers.direction === direction_type.INBOUND
            ? 'InboundEmails'
            : 'OutboundEmails';
        const emails: Array<SearchEmailResponse> = response[responseType].map(
          (email: SearchEmailResponse) => {
            return {
              from: email.from,
              to: email.to,
              subject: email.subject,
              id: email.id,
              sent: email.createdAt,
              attachment:
                pageReloadTriggers.direction === direction_type.INBOUND
                  ? email.Attachments.length - 1
                  : email.Attachments.length,
              status: email.status,
              pageInfo: email.Info,
              isResent:
                pageReloadTriggers.direction === direction_type.OUTBOUND
                  ? email.isResent
                  : false
            };
          }
        );
        setPageData((p: any) => ({
          ...p.pageData,
          emails,
          pageInfo: {
            count: response.Info.count,
            currentPage:
              response.Info.offset /
                pageReloadTriggers.pageInfo?.recordsPerPage +
              1,
            totalPages:
              response.Info.count / pageReloadTriggers.pageInfo?.recordsPerPage
          }
        }));
      } catch (err) {
        setApiError(
          'Unable to fetch records. Please click the refresh button to retry or try shortening the date range.'
        );
        setPageData((p: any) => ({ ...p.pageData, emails: null }));
      } finally {
        setIsLoading(false);
      }
    };
    if (isPageLoadedRef.current) {
      fetchAPI();
    } else {
      isPageLoadedRef.current = true;
      setIsModalOpen(true);
      setPageData((p: any) => ({ ...p, emails: [] }));
    }
  }, [pageReloadTriggers]);

  const pageInfo = {
    currentPage: pageData.pageInfo?.currentPage,
    pageSize: pageReloadTriggers.pageInfo?.recordsPerPage,
    totalItems: pageData.pageInfo?.count,
    handleFetchNextPage
  };
  return (
    <>
      {isModalOpen &&
        ReactDOM.createPortal(
          <ApplyFilterForm
            handleFilter={handleSetFilter}
            direction={pageReloadTriggers.direction}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            handleOpenCloseFilter={handleOpenCloseFilter}
            pageWrapperElem={pageWrapper.current}
          />,
          document.querySelector(`#${APP}`) || document.body
        )}

      <div
        className="search-emails-main-container container--wrapper"
        ref={pageWrapper}
      >
        <MainHeaderContainer enableHeaderGlobalBar />
        <MainContainer>
          <div className="search-emails-body-wrapper">
            <section className="search-emails-header--wrapper">
              {apiError && (
                <ToastNotification
                  lowContrast
                  caption=""
                  iconDescription="Error fetching api"
                  title={apiError}
                  onCloseButtonClick={() => setApiError(null)}
                />
              )}
              <div className="search-emails-header">
                <h1 className="main-heading">Emails</h1>
                <Button
                  hasIconOnly
                  className="search-emails-refresh-button"
                  renderIcon={Renew16}
                  tooltipAlignment="center"
                  tooltipPosition="bottom"
                  iconDescription="Refresh"
                  kind="ghost"
                  size="field"
                  onClick={handleRefreshPage}
                />
                <Button
                  hasIconOnly
                  className={'search-email-filter-button'}
                  renderIcon={Filter16}
                  tooltipAlignment="center"
                  tooltipPosition="bottom"
                  iconDescription="Filter"
                  kind="ghost"
                  size="field"
                  onClick={() => handleOpenCloseFilter(true)}
                />
                <div className="search-email-switcher">
                  <ContentSwitcher
                    selectedIndex={
                      direction_type.INBOUND === pageReloadTriggers.direction
                        ? 0
                        : 1
                    }
                  >
                    <Switch
                      id="Inbound"
                      name="Inbound"
                      text="Inbound"
                      onClick={() =>
                        handleSetDirection(direction_type.INBOUND, filterValues)
                      }
                    />
                    <Switch
                      id="Outbound"
                      name="Outbound"
                      text="Outbound"
                      onClick={() =>
                        handleSetDirection(
                          direction_type.OUTBOUND,
                          filterValues
                        )
                      }
                    />
                  </ContentSwitcher>
                </div>
              </div>
            </section>
            <section className="search-emails-content--wrapper">
              {pageData?.emails !== null && (
                <DisplayEmails
                  emails={pageData?.emails}
                  pageInfo={pageInfo}
                  direction={pageReloadTriggers.direction}
                  setApiError={setApiError}
                  handleSendToOther={onSendToOther}
                />
              )}
            </section>
            <SendToOtherModal
              isModalOpen={showSendToOtherModal}
              onModalClose={() => setShowSendToOtherModal(false)}
              onClickSend={onClickSend}
            />
          </div>
        </MainContainer>
        {(isResending || isLoading) && <Loading withOverlay={true} />}
      </div>
    </>
  );
};

SearchEmails.displayName = 'SearchEmails';
export default SearchEmails;
